<template>
    <ContentComp
        :load-state="LoadState"
        :style="{
            'min-height': '175px',
        }"
    >
        <FormLayout>
            <FormLayoutRow v-if="!model.raceContestantId && !model.bib">
                <FormLayoutLabel>
                    {{ $tm('contestants.bib') }}
                </FormLayoutLabel>
                <InputNullableInt v-model="model.bib" />
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel>
                    {{ $tm('racecontestanttime.stationid') }}
                </FormLayoutLabel>
                <InputSelect
                    v-model="model.stationId"
                    :datasource="stationDatasource"
                    :disabled="model.forceFinish === true"
                />
            </FormLayoutRow>

            <FormLayoutRow class="mt-4">
                <FormLayoutLabel>
                    {{ $tm('general.passingtime') }}
                </FormLayoutLabel>
                <InputDateTime
                    v-if="!eventDate"
                    v-model="model.time!"
                    format="DATETIMEMS3"
                    :hide-selector="true"
                />
                <div
                    v-else
                    class="d-flex flex-nowrap"
                >
                    <InputDateTime
                        v-model="model.time!"
                        format="TIMEMS3"
                        :hide-selector="true"
                        :defaultDateForTimeOnlyInput="eventDate"
                    />
                    <small class="pt-2 ms-3 text-muted">
                        <DateComp
                            :value="eventDate"
                            format="DATE"
                            class="mt-2"
                        />
                    </small>
                </div>
            </FormLayoutRow>

            <FormLayoutRow class="mt-4">
                <FormLayoutLabel>
                    {{ $tm('settime.forcefinish') }}
                </FormLayoutLabel>
                <InputBool
                    v-model="model.forceFinish"
                    @click="onForceFinishClick"
                    tooltip="addtime.forcefinishtooltip"
                />
            </FormLayoutRow>

            <div v-show="showForcedLap">
                <FormLayoutRow class="mt-4">
                    <FormLayoutLabel>
                        {{ $tm('settime.forcelap') }}
                    </FormLayoutLabel>
                    <div class="w-25">
                        <InputNullableInt
                            v-model="model.forcedLap"
                            ref="inputForcedLap"
                        />
                    </div>
                </FormLayoutRow>
            </div>
        </FormLayout>
    </ContentComp>
</template>

<script setup lang="ts">
import { ContentComp, DateComp, InputBool, InputDateTime, InputInt, InputNullableInt, InputSelect } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { useHelper, useLoaders, useTaskLoader } from '@/composeables';
import type { ISelectLineDatasource } from '@/objects/ISelectLineDatasource';
import { useEventCommonStoreRef } from '@/stores';
import { computed, nextTick, ref, watch } from 'vue';
import type { AddEditTimeRequest } from './AddEditTimeRequest';

/**
 * This will add or edit a time for a contestant
 * EDIT means we disable the previous time and add a new one via the API. We NEVER actually change the time. Everything is handled via transactions
 *
 * There are no required properties on the request. If none is provided the user will have to provide them in the dialog. This is useful when adding a new time
 *
 * NOTE! An transaction needs at the very minimum a Station, Bib and Time
 */

const model = defineModel<AddEditTimeRequest>({ required: true });

const inputForcedLap = ref<HTMLInputElement | null>(null);

// Preload necessary data
const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['EVENT', 'STATIONS', 'STATIONROLES']);

const { Event, Stations, Groups, StationRoles } = useEventCommonStoreRef();

// We get the date from the event. This is only if the event is for a single day. Then we only need a time input and not a date input
const eventDate = ref<Date | null>((Event.value?.EventStart.getDay() == Event.value?.EventEnd.getDay() ? Event.value?.Date : null) ?? null);

// We create a default time right NOW but set the milliseconds to 0
const defaultTime = new Date(new Date().setMilliseconds(0));
if (eventDate.value) {
    defaultTime.setFullYear(eventDate.value.getFullYear(), eventDate.value.getMonth(), eventDate.value.getDate());
}

// Default values
model.value.time = !model.value.elapsedTime && !model.value.startingTime && !model.value.time ? defaultTime : model.value.time;
model.value.raceContestantId = model.value.raceContestantId ?? null;
model.value.stationId = model.value.stationId ?? null;
model.value.bib = model.value.bib ?? null;
model.value.forceFinish = model.value.forceFinish ?? false;
model.value.forcedLap = model.value.forcedLap ?? null;
model.value.tuid = model.value.tuid ?? null;

const showForcedLap = computed(() => {
    return Groups.value.some((g) => (g.NumberOfLaps ?? 0) > 1) && model.value.forceFinish === true;
});

/**
 * Set up the station datasource
 */
const stationDatasource = computed<ISelectLineDatasource>(() => {
    const datasource: ISelectLineDatasource = { source: [] };

    const stations = Stations.value.map((s) => {
        return {
            value: s.ID,
            text: s.Name ?? '',
        };
    });

    // Sort the stations by name
    stations.sort((a, b) => a.text.localeCompare(b.text));
    datasource.source = stations;

    return datasource;
});

const onForceFinishClick = () => {
    useHelper().setFocus(false, inputForcedLap.value);
};
</script>
